import React from 'react'
import { Box, Chip, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'

// Eventos del log que ocurrieron DESPUES de que el core le respondio al punto de venta.
//
// Existen porque la venta no siempre termina cuando el comercio recibe su respuesta:
// cuando el proveedor no contesta a tiempo se le responde "tiempo de espera agotado", se
// le devuelve el saldo y el core sigue preguntando unos segundos mas (la ventana de
// evidencia). Si en esa ventana el proveedor acaba diciendo que si la aplico, queda una
// venta cobrada al cliente, devuelta al comercio y aprobada por el proveedor. Eso es lo
// que esta seccion saca a la luz.
//
// Tres reglas mandan sobre lo que se pinta aqui:
//   1. El corte es la POSICION del ultimo TXN_END, no el campo `afterClose` de los datos.
//      Ese campo significa otra cosa y hay eventos posteriores al cierre que lo traen en
//      falso: usarlo daria el resultado contrario.
//   2. Sin TXN_END no hay seccion. Sin el corte no se puede saber que es posterior, y
//      afirmarlo seria inventarlo.
//   3. La cola normal no es una incidencia. Un log que termina en evidencia registrada no
//      tiene nada de raro; solo la aprobacion tardia significa dinero y solo ella se
//      destaca. Si todo se pintara como error, nadie volveria a creerle a la seccion.

const styles = {
  titulo: { fontSize: 12, fontWeight: 'bold', marginBottom: 0.5 },
  nota: { fontSize: 11, color: 'text.secondary', marginBottom: 0.5 },
  aviso: {
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'action.hover',
    padding: 1.5,
    marginTop: 1
  },
  avisoTitulo: { fontSize: 11, fontWeight: 'bold', color: 'error.main', marginBottom: 0.5 },
  encabezado: { fontSize: 11, padding: '4px 8px' },
  celda: { fontSize: 11, padding: '4px 8px' },
  datos: { fontSize: 10, color: 'text.secondary', wordBreak: 'break-all' },
  chip: { fontSize: 10, height: 18 }
}

// El catalogo de codigos que el core puede emitir, leido en espanol. Un codigo que no
// este aqui se pinta con su codigo original: perder el evento seria peor que verlo crudo.
const EVENTOS = {
  TXN_START: { texto: 'Inicia la transacción', color: 'default' },
  TXN_END: { texto: 'Responde al comercio', color: 'default' },
  AUTH_OK: { texto: 'Credenciales válidas', color: 'default' },
  AUTH_FAIL: { texto: 'Credenciales rechazadas', color: 'error' },
  PRODUCT_SELECTED: { texto: 'Producto seleccionado', color: 'default' },
  BALANCE_RESERVED: { texto: 'Saldo reservado', color: 'default' },
  BALANCE_DISCOUNT: { texto: 'Saldo descontado', color: 'default' },
  BALANCE_REFUNDED: { texto: 'Saldo devuelto', color: 'warning' },
  BALANCE_INSUFFICIENT: { texto: 'Saldo insuficiente', color: 'error' },
  PROVIDER_REQUEST: { texto: 'Petición al proveedor', color: 'default' },
  PROVIDER_RESPONSE: { texto: 'Respuesta del proveedor', color: 'default' },
  PROVIDER_RETRY: { texto: 'Consulta de estatus al proveedor', color: 'default' },
  PROVIDER_TIMEOUT: { texto: 'El proveedor no respondió a tiempo', color: 'warning' },
  PROVIDER_ERROR: { texto: 'Error del proveedor', color: 'error' },
  BACKUP_SWITCH: { texto: 'Se redirigió al proveedor de respaldo', color: 'warning' },
  EVIDENCE_WINDOW_START: { texto: 'Abre la ventana de evidencia', color: 'default' },
  EVIDENCE_WINDOW_END: { texto: 'Cierra la ventana de evidencia', color: 'default' },
  EVIDENCE_RECORDED: { texto: 'Evidencia registrada', color: 'warning' },
  LATE_APPROVAL_ALERT: { texto: 'El proveedor aplicó la operación tarde', color: 'error' },
  ERROR_MAPPED: { texto: 'Código de error homologado', color: 'default' },
  ERROR_DEFAULTED: { texto: 'Error sin homologar: se respondió denegada', color: 'warning' },
  DUPLICATE_BLOCKED: { texto: 'Bloqueada por duplicada', color: 'warning' },
  RECENT_RECHARGE_BLOCKED: { texto: 'Bloqueada por recarga reciente', color: 'warning' },
  SECURITY_LIMIT_BLOCKED: { texto: 'Bloqueada por límite de seguridad', color: 'warning' },
  SALE_QUERY: { texto: 'Consulta FindSaleByTrace sobre la venta', color: 'default' },
  WAITING: { texto: 'Esperando respuesta', color: 'default' }
}

// Los campos de la aprobacion tardia, en el orden en que se leen. Es lo unico de esta
// seccion que se pinta campo a campo: del resto de eventos se muestra su `data` tal cual,
// porque cada codigo trae los suyos y catalogarlos todos seria otra spec.
const CAMPOS_APROBACION_TARDIA = [
  { clave: 'providerCode', etiqueta: 'Código del proveedor' },
  { clave: 'respondedToClient', etiqueta: 'Se le respondió al comercio' },
  { clave: 'serviceName', etiqueta: 'Conector' },
  { clave: 'referencia', etiqueta: 'Referencia del proveedor' }
]

const CODIGO_APROBACION_TARDIA = 'LATE_APPROVAL_ALERT'
const CODIGO_FIN = 'TXN_END'

const vacio = (val) => val === null || val === undefined || val === ''

const texto = (val) => (vacio(val) ? '-' : String(val))

const booleano = (val) => {
  if (val === true) return 'Sí'
  if (val === false) return 'No'
  return '-'
}

const momento = (ms) => {
  if (vacio(ms) || isNaN(ms)) return '-'
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`
}

// Los datos de un evento, en una linea legible. Se omiten las claves cuyo valor es un
// objeto: ninguna de las que manda el core hoy lo es, y volcar un objeto pintaria
// [object Object].
const datosLegibles = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return ''
  return Object.keys(data)
    .filter((clave) => typeof data[clave] !== 'object' || data[clave] === null)
    .map((clave) => `${clave}: ${vacio(data[clave]) ? '-' : String(data[clave])}`)
    .join(' · ')
}

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 160, marginBottom: 1, marginRight: 3 }}>
    <Typography sx={{ fontSize: 10, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, wordBreak: 'break-all' }}>{children}</Typography>
  </Box>
)

/**
 * Los eventos posteriores al cierre de la venta.
 *
 * `events` es la lista que elige TxnLogView: la del documento cuando llega (superconjunto)
 * y la del resumen cuando no. No se pinta nada si no hay cola: la venta que termina donde
 * dice que termina es el caso normal y no merece una linea para decirlo.
 */
const PostCloseEventsView = ({ events }) => {
  // Solo cuentan los eventos con codigo de texto. El core llego a guardar como codigo un
  // documento entero (un println con los argumentos corridos, 30/09/2026) y pintarlo
  // tumbaba el panel; esos registros siguen en la base.
  const lista = Array.isArray(events)
    ? events.filter((evento) => evento && typeof evento === 'object' && typeof evento.code === 'string' && evento.code)
    : []

  // El corte: lo que viene detras del ultimo fin de transaccion.
  const corte = lista.map((evento) => evento.code).lastIndexOf(CODIGO_FIN)
  const posteriores = corte >= 0 ? lista.slice(corte + 1) : []

  if (posteriores.length === 0) return null

  const aprobacionTardia = posteriores.find((evento) => evento.code === CODIGO_APROBACION_TARDIA)
  const datosTardia = (aprobacionTardia && aprobacionTardia.data) || {}

  return (
    <>
      <Divider sx={{ marginY: 1 }} />
      <Typography sx={styles.titulo}>Después de responder al comercio</Typography>
      <Typography sx={styles.nota}>
        {posteriores.length === 1
          ? 'Este evento ocurrió después de que el punto de venta ya recibió su respuesta, así que no cambia el resultado que se lee arriba.'
          : 'Estos eventos ocurrieron después de que el punto de venta ya recibió su respuesta, así que no cambian el resultado que se lee arriba.'}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...styles.encabezado, width: 90 }}>Momento</TableCell>
            <TableCell sx={{ ...styles.encabezado, width: 260 }}>Evento</TableCell>
            <TableCell sx={styles.encabezado}>Datos</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {posteriores.map((evento, index) => {
            const catalogado = EVENTOS[evento.code]
            const datos = datosLegibles(evento.data)
            return (
              <TableRow key={index}>
                <TableCell sx={styles.celda}>{momento(evento.at)}</TableCell>
                <TableCell sx={styles.celda}>
                  <Chip
                    size="small"
                    label={catalogado ? catalogado.texto : evento.code}
                    color={catalogado ? catalogado.color : 'default'}
                    sx={styles.chip}
                  />
                </TableCell>
                <TableCell sx={styles.celda}>
                  <Typography sx={styles.datos}>{datos || '-'}</Typography>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {aprobacionTardia && (
        <Box sx={styles.aviso}>
          <Typography sx={styles.avisoTitulo}>El proveedor aplicó la operación tarde</Typography>
          <Typography sx={styles.nota}>
            El proveedor confirmó la operación después de que se le respondió al comercio. El resultado que ve el
            comercio y el del proveedor no coinciden.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {CAMPOS_APROBACION_TARDIA.map(({ clave, etiqueta }) => (
              <Field key={clave} label={etiqueta}>{texto(datosTardia[clave])}</Field>
            ))}
            <Field label="Cuándo se supo">{momento(datosTardia.elapsedMs)}</Field>
            <Field label="Reembolso por reclamar">{booleano(datosTardia.refundClaim)}</Field>
          </Box>
          {datosTardia.refundClaim === true && (
            <Typography sx={styles.avisoTitulo}>
              Hay un reembolso por reclamar al proveedor: se le devolvió el saldo al comercio y la operación sí se aplicó.
            </Typography>
          )}
        </Box>
      )}
    </>
  )
}

export default PostCloseEventsView
